import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, Validators } from '@angular/forms';
import { CeRequestOptions } from '../../request-header/request-header.component';
import { SamplingGrowthStageViewModel, SamplingTissueTypeViewModel, SamplingLaboratoryViewModel, SamplingAssayViewModel, SamplingTissueCollectionTypeViewModel, SamplingDateCalculationStrategy } from './sampling-needs-form/sampling-needs-form.component';
import { ProjectedTaskConfiguration, TaskType } from '../../../shared/services/projected-task-configuration.service';
import { ExperimentDetailsFormModel } from '../../services/request-form.service';
import { TissueCollectionType } from '../../../shared/services/request.service';

@Component({
  selector: 'app-sampling-needs',
  templateUrl: './sampling-needs.component.html'
})
export class SamplingNeedsComponent implements OnInit {

  @Input() ceRequestOptions: CeRequestOptions;
  @Input() experimentDesignFormGroup: FormGroup;
  @Input() requestDetails: ExperimentDetailsFormModel;
  
  public laboratories: SamplingLaboratoryViewModel[];
  public tissueTypes: SamplingTissueTypeViewModel[] = [];
  public growthStages: SamplingGrowthStageViewModel[] = [];
  public tissueCollectionTypes: SamplingTissueCollectionTypeViewModel[] = [];
  public projectedTaskConfigurations: ProjectedTaskConfiguration[] = [];

  selectedLaboratoryId: number = null;

  constructor(private formBuilder: FormBuilder) {
  }

  public get samplingNeeds(): FormArray {
    return <FormArray>this.experimentDesignFormGroup.get('requestedSamplings');
  }

  public addAnalysis() {
    const lab = this.laboratories.find(l => l.id === this.selectedLaboratoryId);
    
    if (!lab) return;

    const newGroup = this.formBuilder.group({
      laboratory: this.formBuilder.control(lab.id, Validators.required),
      tissueType: this.formBuilder.control(null, Validators.required),
      assay: this.formBuilder.control(null, Validators.required),
      tissueCollectionType: this.formBuilder.control(null, Validators.required),
      samplingDateCalculationStrategy: this.formBuilder.control(SamplingDateCalculationStrategy.GrowthStage),
      growthStage: this.formBuilder.control(null),
      daysAfterPlanting: this.formBuilder.control(null),
      order: this.samplingNeeds.controls.length + 1
    });
    this.samplingNeeds.push(newGroup);

    this.selectedLaboratoryId = null;
  }

  public remove(index: number) {
    this.samplingNeeds.removeAt(index);

    this.samplingNeeds.controls.forEach((c, i) => {
      c.get('order').setValue(i + 1);
    });
  }

  public ngOnInit(): void {
    this.laboratories = this.ceRequestOptions
      .laboratories
      .filter(l => l.isActive)
      .sort(this.alphabeticalNamedSorter)
      .map(l => <SamplingLaboratoryViewModel>{
        id: l.id,
        display: l.name
      });

    this.projectedTaskConfigurations = this.ceRequestOptions
      .projectedTaskConfigurations
      .filter(s => s.isActive && s.taskType === TaskType.Sampling && s.cropId === this.requestDetails.crop);

    this.growthStages = this.ceRequestOptions
      .growthStages
      .filter(l => l.isActive)
      .sort(this.orderFieldSorter)
      .filter(g => this.projectedTaskConfigurations.some(p => p.growthStageId === g.id))
      .map(g => <SamplingGrowthStageViewModel>{
        id: g.id,
        display: g.name
      });

    this.tissueTypes = this.ceRequestOptions
      .tissueTypes
      .filter(t => t.isActive)
      .sort(this.alphabeticalNamedSorter)
      .map(t => <SamplingTissueTypeViewModel>{
        id: t.id,
        display: t.name
      });

      this.tissueCollectionTypes = ([ TissueCollectionType.Standard, TissueCollectionType.Custom ])
        .map(tissueCollectionTypeId => ({
          id: tissueCollectionTypeId,
          display: TissueCollectionType[tissueCollectionTypeId]
        }));
  }

  public assaysForLab(labId: number): SamplingAssayViewModel[] {
    const labModel = this.ceRequestOptions.laboratories.find(f => f.id === labId);

    if (!labModel){
      return [];
    } 

    return labModel
      .assays
      .filter(a => a.isActive)
      .map(m => <SamplingAssayViewModel>{ id: m.id, display: m.name });
  }

  public nameOfLab(labId: number): string {
    const labModel = this.ceRequestOptions.laboratories.find(l => l.id === labId);
    if (!labModel)
      return 'Error';

    return labModel.name;
  }

  private alphabeticalNamedSorter(l1: { name: string }, l2: { name: string }): number {
    const l1Name = (l1.name || '').toUpperCase();
    const l2Name = (l2.name || '').toUpperCase();

    if (l1Name > l2Name)
      return 1;
    else if (l1Name < l2Name)
      return -1;
    else 
      return 0;
  }

  private orderFieldSorter(l1: { order: number }, l2: {order: number }): number {
    if (l1.order > l2.order)
      return 1;
    else if (l1.order < l2.order)
      return -1;
    else
      return 0;
  }
}


// WEBPACK FOOTER //
// F:/PHAZP5152IS01-4/_work/202/s/src/app/request-creation/experiment-design/sampling-needs/sampling-needs.component.ts